// src/blogs/glassPanels.js
// Tiny registry linking DOM posts to the 3D backdrop. A post element opts in
// with `ref={glassPanelRef}`; the backdrop's scene (postScenes/GlassPanels.jsx)
// lives in a different React tree (its own <Canvas>), so a module-level store
// is the simplest bridge — the same trick useSharedAudioAnalyser used.
//
// Only elements near the viewport are published, via one shared
// IntersectionObserver, so the scene mounts a glass mesh (and reads a layout
// rect each frame) for just the handful of posts actually on screen, however
// long the infinite feed grows.
let nextId = 1;
const entries = new Map(); // element -> { id, el }
const visible = new Set(); // entries currently near the viewport
const listeners = new Set();
let snapshot = []; // replaced (never mutated) whenever `visible` changes
let observer = null;

function publish() {
  snapshot = [...visible];
  listeners.forEach((listener) => listener());
}

function getObserver() {
  if (!observer) {
    observer = new IntersectionObserver(
      (records) => {
        let changed = false;
        for (const record of records) {
          const entry = entries.get(record.target);
          if (!entry) continue;
          if (record.isIntersecting) {
            if (!visible.has(entry)) {
              visible.add(entry);
              changed = true;
            }
          } else if (visible.delete(entry)) {
            changed = true;
          }
        }
        if (changed) publish();
      },
      // A little slack so a panel exists just before it scrolls into view.
      { rootMargin: "150px 0px" }
    );
  }
  return observer;
}

// Stable-identity ref callback (React 19: the returned function is the
// cleanup, run when the element unmounts).
export function glassPanelRef(el) {
  if (!el) return undefined;
  const entry = { id: nextId++, el };
  entries.set(el, entry);
  getObserver().observe(el);
  return () => {
    observer?.unobserve(el);
    entries.delete(el);
    if (visible.delete(entry)) publish();
  };
}

// For useSyncExternalStore.
export function subscribeGlassPanels(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getGlassPanels() {
  return snapshot;
}
